// https://github.com/jackocnr/intl-tel-input/issues/1596#issuecomment-2105630861

import type { ChangeEvent, MutableRefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import type { Nullable } from 'corgi-types';
import type { Iti } from 'intl-tel-input';
import intlTelInput from 'intl-tel-input';

import { useLocalization } from '../../providers/LocaleProvider';
import type { IntlTelInputChangeResult } from '../../types/tel';
import { getTelNumber, isValueValid, sanitizeInputNumber } from './utils';

export const enum IntlTelInputEvent {
  CountryChange = 'countrychange',
}

const RELEVANT_COUNTRIES = ['de', 'at', 'ch', 'fr', 'it', 'be', 'nl'];
// Render only limited number of counties in Jest tests to make them faster
const LIMIT_COUNTRIES = process.env.NODE_ENV === 'test' ? RELEVANT_COUNTRIES : null;

interface UseItiInitializerOptions {
  elementRef: MutableRefObject<HTMLInputElement>;
  itiRef: MutableRefObject<Iti>;
  handleCountryChange: () => void;
  setInputValue: (value: string) => void;
}

interface UseItiOptions {
  elementRef: MutableRefObject<HTMLInputElement>;
  initialValue: string;
  onChange: (value: Nullable<IntlTelInputChangeResult>) => void;
  onBlur: (value: Nullable<IntlTelInputChangeResult>) => void;
}

const loadTranslations = async (language: string) => {
  if (language === 'de') {
    const { default: de } = await import('intl-tel-input/build/js/i18n/de/index.js');

    return de;
  } else if (language === 'fr') {
    const { default: fr } = await import('intl-tel-input/build/js/i18n/fr/index.js');

    return fr;
  } else if (language === 'it') {
    const { default: it } = await import('intl-tel-input/build/js/i18n/it/index.js');

    return it;
  } else if (language === 'nl') {
    const { default: nl } = await import('intl-tel-input/build/js/i18n/nl/index.js');

    return nl;
  }
};

const useItiInitializer = ({
  elementRef,
  itiRef,
  handleCountryChange,
  setInputValue,
}: UseItiInitializerOptions) => {
  const { region, language } = useLocalization();

  const countryChangeHandlerRef = useRef(handleCountryChange);
  countryChangeHandlerRef.current = handleCountryChange;

  useEffect(() => {
    let iti: Iti;

    (async () => {
      const i18n = await loadTranslations(language);

      const element = elementRef.current;

      iti = intlTelInput(element, {
        initialCountry: region.toLowerCase(),
        separateDialCode: true,
        nationalMode: false,
        autoPlaceholder: 'off',
        countrySearch: false,
        countryOrder: RELEVANT_COUNTRIES,
        ...(LIMIT_COUNTRIES && { onlyCountries: LIMIT_COUNTRIES }),
        i18n,
      });
      setInputValue(element.value);

      element.addEventListener(IntlTelInputEvent.CountryChange, () => {
        countryChangeHandlerRef.current();
      });

      itiRef.current = iti;
    })();

    return () => iti?.destroy();
  }, [region, language]);
};

export const useIti = ({ elementRef, initialValue, onChange, onBlur }: UseItiOptions) => {
  const itiRef = useRef<Iti>(undefined);
  const [inputValue, setInputValue] = useState(initialValue);

  const handleCountryChange = () => {
    const telNumber = getTelNumber({ iti: itiRef.current, tel: elementRef.current.value });
    onChange(telNumber);
    setInputValue(telNumber?.number ?? '');
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const inputNumber = sanitizeInputNumber(event.target.value);

    if (isValueValid(inputNumber)) {
      const telNumber = getTelNumber({ iti: itiRef.current, tel: inputNumber });
      onChange(telNumber);
      setInputValue(telNumber?.number ?? '');
    }
  };

  const handleBlur = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    onBlur(getTelNumber({ iti: itiRef.current, tel: value }));
  };

  useItiInitializer({ elementRef, itiRef, setInputValue, handleCountryChange });

  return { inputValue, handleChange, handleBlur };
};
